/**
 * Image Workflows Page
 * Visual workflow editor for chaining image operations
 */

import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WorkflowList, WorkflowEditor } from '@/features/image-workflows';
import type { Workflow } from '@/types/imageWorkflow';

type ViewMode = 'list' | 'editor';

export function ImageWorkflowsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlWorkflowId = searchParams.get('workflowId');
  const urlExecutionId = searchParams.get('executionId');

  const [viewMode, setViewMode] = useState<ViewMode>(urlWorkflowId ? 'editor' : 'list');
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(urlWorkflowId);
  const [initialExecutionId, setInitialExecutionId] = useState<string | null>(urlExecutionId);

  useEffect(() => {
    if (urlWorkflowId) {
      setSelectedWorkflowId(urlWorkflowId);
      setInitialExecutionId(urlExecutionId);
      setViewMode('editor');
      setSearchParams({}, { replace: true });
    }
  }, [urlWorkflowId, urlExecutionId, setSearchParams]);

  // Handle workflow selection
  const handleSelectWorkflow = useCallback((workflow: Workflow) => {
    setSelectedWorkflowId(workflow.id);
    setViewMode('editor');
  }, []);

  // Handle new workflow
  const handleNewWorkflow = useCallback(() => {
    setSelectedWorkflowId(null);
    setViewMode('editor');
  }, []);

  // Handle back to list
  const handleBack = useCallback(() => {
    setViewMode('list');
    setSelectedWorkflowId(null);
    setInitialExecutionId(null);
  }, []);

  // Handle save (refresh list if needed)
  const handleSave = useCallback((workflow: Workflow) => {
    setSelectedWorkflowId(workflow.id);
  }, []);

  return (
    <div className="h-[calc(100vh-4rem)]">
      {viewMode === 'list' ? (
        <div className="h-full overflow-y-auto p-6 bg-gray-50">
          <WorkflowList
            onSelect={handleSelectWorkflow}
            onNew={handleNewWorkflow}
          />
        </div>
      ) : (
        <WorkflowEditor
          workflowId={selectedWorkflowId}
          initialExecutionId={initialExecutionId}
          onBack={handleBack}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
